import React, { useEffect, useMemo, useState } from "react";
import { Tour, ConfigProvider } from "antd";
import ptBR from "antd/locale/pt_BR";
import { useAuth } from "../../hooks/auth";
import { getProjectsSteps } from "./onboarding/ProjectsOnboarding.jsx";
import { getSyncsSteps } from "./onboarding/SyncsOnboarding.jsx";
import { getReportsSteps } from "./onboarding/ReportsOnboarding.jsx";
import { getGroupsSteps } from "./onboarding/GroupsOnboarding.jsx";
import { getBuilderSteps } from "./onboarding/BuilderOnboarding.jsx";
import mixpanel from "mixpanel-browser";

const OnboardingManager = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [mandatory, setMandatory] = useState(false);
  const [mode, setMode] = useState(null); // 'full' | 'projects' | 'syncs' | 'reports' | 'groups'
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const tenantId = user?.tenant_id || user?.tenantId || "default";
  const userId = user?.id || user?.userId || user?.userName || user?.user_name || "anon";
  const storageKey = `onboarding_progress:${tenantId}:${userId}`;
  const autoStartEnabled = (process.env.REACT_APP_ONBOARDING_AUTO_START ?? 'false') === 'true';

  const saveProgress = (payload) => {
    const base = {
      sequence: ["projects", "syncs", "reports", "groups"],
      currentModuleIndex,
      currentStepIndex,
      mode,
      mandatory,
      updatedAt: Date.now(),
    };
    try {
      localStorage.setItem(storageKey, JSON.stringify({ ...base, ...payload }));
    } catch {}
  };

  const loadProgress = () => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const progress = loadProgress();
    if (!progress && autoStartEnabled) {
      setMode("full");
      setMandatory(true);
      setCurrentModuleIndex(0);
      setCurrentStepIndex(0);
      setOpen(true);
      saveProgress({ startedAt: Date.now() });
      try { mixpanel.track("onboarding_start", { mode: "full", tenantId, userId }); } catch {}
    }
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail || {};
      const requested = detail.type || "full";
      setMode(requested);
      setMandatory(false);
      setCurrentModuleIndex(0);
      setCurrentStepIndex(0);
      if (requested === 'builder') {
        const waitFor = (selector, timeout = 8000) => new Promise((resolve, reject) => {
          const start = Date.now();
          const tick = () => {
            const el = document.querySelector(selector);
            if (el) return resolve(el);
            if (Date.now() - start > timeout) return reject(new Error('timeout'));
            requestAnimationFrame(tick);
          };
          tick();
        });

        saveProgress({ startedAt: Date.now() });
        try { mixpanel.track("onboarding_start", { mode: requested, tenantId, userId }); } catch {}
        window.dispatchEvent(new Event('tour-open-builder'));

        (async () => {
          try {
            await waitFor('[data-tour-id="builder-title-input"]');
            setOpen(true);
          } catch {
            setOpen(true);
          }
        })();
      } else {
        setOpen(true);
        saveProgress({ startedAt: Date.now() });
        try { mixpanel.track("onboarding_start", { mode: requested, tenantId, userId }); } catch {}
      }
    };
    window.addEventListener("start-onboarding", handler);
    return () => window.removeEventListener("start-onboarding", handler);
  }, []);

  const sequence = ["projects", "syncs", "reports", "groups"];
  const activeModule = useMemo(() => {
    if (mode === "full") return sequence[currentModuleIndex];
    return mode;
  }, [mode, currentModuleIndex]);

  const steps = useMemo(() => {
    if (activeModule === "projects") return getProjectsSteps();
    if (activeModule === "syncs") return getSyncsSteps();
    if (activeModule === "reports") return getReportsSteps();
    if (activeModule === "groups") return getGroupsSteps();
    if (activeModule === "builder") return getBuilderSteps();
    return [];
  }, [activeModule]);

  const locale = { ...ptBR, Tour: { Next: "Próximo", Previous: "Anterior", Finish: "Concluir" } };

  const handleChange = (current) => {
    setCurrentStepIndex(current);
    saveProgress({ currentStepIndex: current });
    try { mixpanel.track("onboarding_step_change", { module: activeModule, step: current }); } catch {}
  };

  const handleClose = () => {
    if (mandatory) {
      // Bloquear fechamento no primeiro acesso
      setOpen(true);
      return;
    }
    setOpen(false);
    saveProgress({ closedAt: Date.now() });
    try { mixpanel.track("onboarding_closed", { module: activeModule, mandatory }); } catch {}
  };

  const actionsRender = (originNode, info) => {
    const total = steps.length;
    const current = info.current + 1;
    return (
      <div style={{ width: "100%" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <span style={{ fontSize: 12 }}>Etapa {current} de {total}</span>
          <div style={{ flex: 1, marginLeft: 12, height: 6, background: "#f0f0f0", borderRadius: 4 }}>
            <div style={{ width: `${(current / total) * 100}%`, height: "100%", background: "#1677ff", borderRadius: 4, transition: "width 0.25s ease" }} />
          </div>
        </div>
        {originNode}
      </div>
    );
  };

  const handleFinish = () => {
    if (mode === "full") {
      if (currentModuleIndex < sequence.length - 1) {
        // Próximo módulo
        const next = currentModuleIndex + 1;
        setCurrentModuleIndex(next);
        setCurrentStepIndex(0);
        saveProgress({ currentModuleIndex: next, currentStepIndex: 0 });
        try { mixpanel.track("onboarding_module_complete", { module: activeModule }); } catch {}
        return;
      }
      // Finalizou todos
      setMandatory(false);
      setOpen(false);
      saveProgress({ completedAt: Date.now(), mandatory: false });
      try { mixpanel.track("onboarding_complete_all", { duration_ms: (Date.now() - (loadProgress()?.startedAt || Date.now())) }); } catch {}
    } else {
      setOpen(false);
      saveProgress({ completedAt: Date.now() });
      try { mixpanel.track("onboarding_module_complete", { module: activeModule }); } catch {}
      if (mode === 'builder') {
        try { window.history.back(); } catch {}
      }
    }
  };

  useEffect(() => {
    const closeSaveTemplate = () => {
      const modal = document.querySelector('[data-tour-id="builder-save-template-modal"]');
      if (modal) {
        const closeBtn = modal.querySelector('.ant-modal-close') || modal.querySelector('[aria-label="Close"]');
        if (closeBtn) closeBtn.click();
      }
    };
    window.addEventListener('tour-close-save-template-modal', closeSaveTemplate);
    return () => window.removeEventListener('tour-close-save-template-modal', closeSaveTemplate);
  }, []);

  if (!activeModule) return null;

  return (
    <ConfigProvider locale={locale}>
      <Tour
        open={open}
        steps={steps}
        onClose={handleClose}
        onChange={handleChange}
        mask
        disabledInteraction={mandatory}
        closeIcon={mandatory ? null : undefined}
        scrollIntoView
        actionsRender={actionsRender}
        onFinish={handleFinish}
      />
    </ConfigProvider>
  );
};

export default OnboardingManager;